import { useCallback } from 'react'

import {
  type InfiniteData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { buildPaginatedEndpoint, getNextPageParam } from '@/helpers/api'

import { excerptQueryKeys, excerptsQueryKeys } from '@/hooks/excerpts/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginateRequest, PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { Excerpt } from '@/types/library/Excerpt'

interface UseExcerptsArgs<T> {
  orgId: UUID | ''
  noInitialData?: boolean
  selector?: (data: PaginatedResponse<Excerpt>) => T
}

export const fetchExcerpts = async (
  orgId: UUID | '',
  accessToken: string,
  pagination: PaginateRequest,
): Promise<PaginatedResponse<Excerpt>> => {
  if (!orgId) {
    throw new Error('No org ID provided.')
  }
  const url = buildPaginatedEndpoint(
    `${API_URL}/api/v1/organizations/${orgId}/excerpts`,
    pagination,
  )
  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    throw new Error(res.statusText)
  }
  return (await res.json()) as PaginatedResponse<Excerpt>
}

export const useExcerpts = <T = Excerpt[]>({
  orgId,
  selector,
  noInitialData = false,
}: UseExcerptsArgs<T>) => {
  const { accessToken } = useAuthInfoWithThrow()

  const queryClient = useQueryClient()

  const select = useCallback(
    (data: PaginatedResponse<Excerpt>) => {
      if (selector) {
        return selector(data)
      }
      return data.data.sort((a, b) =>
        a.name.localeCompare(b.name),
      ) as unknown as T
    },
    [selector],
  )

  const query = useQuery({
    queryFn: async () => {
      const excerpts = await fetchExcerpts(orgId, accessToken, { limit: 10000 })
      for (const excerpt of excerpts.data) {
        queryClient.setQueryData(
          excerptQueryKeys.excerpt({
            excerptId: excerpt.id,
          }),
          excerpt,
        )
      }
      return excerpts
    },
    enabled: !!orgId,
    initialData: noInitialData
      ? undefined
      : { data: [], has_more: false, count: 0 },
    queryKey: excerptsQueryKeys.organization({ orgId }),
    staleTime: Infinity,
    select,
  })
  return query
}

interface UseExcerptCountArgs {
  orgId: UUID | ''
}

interface ExcerptCount {
  count: number
}

const selectExcerptCount = (data: PaginatedResponse<Excerpt>): ExcerptCount => {
  return { count: data.count }
}

export const useExcerptCount = ({ orgId }: UseExcerptCountArgs) => {
  const { accessToken } = useAuthInfoWithThrow()
  return useQuery({
    queryKey: excerptsQueryKeys.count({ orgId }),
    // TODO: remove this limit
    queryFn: () => fetchExcerpts(orgId, accessToken, { limit: 10000 }),
    enabled: !!orgId && !!accessToken,
    select: selectExcerptCount,
  })
}
